import React, { useEffect, useRef, useState } from 'react';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import { PermissionAccessProvider } from '../../../contexts/PermissionAccessContext';
import { searchPorts } from '../../../services/orderLookups.service';

/**
 * Async cms.ports picker for Order Info · Port of Origin / Discharge. Stores the
 * port recno (ordersall.portoforigin / portofdischarge) while displaying the
 * port name — the same freeText async-search pattern as CarrierLookupField. A
 * pick sets {code: recno, name}; typing free text unlinks (code 0) so a partial
 * name never masquerades as a linked port.
 *
 * Only mounts when the parent decided the field is editable, so it wraps in
 * accessLevel={2} to keep the typeahead active inside any read-only provider
 * (e.g. the order-lc-fields container it renders under on an order).
 */
interface Props {
  /** Current cms.ports recno (0 = unset). */
  code: number;
  /** Current port name (display). */
  name: string;
  /** Fires on pick (code = recno, name) or free-typing (code 0, raw text). */
  onChange: (code: number, name: string) => void;
  ariaLabel: string;
}

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

const PortLookupField: React.FC<Props> = ({ code, name, onChange, ariaLabel }) => {
  const [displayName, setDisplayName] = useState<string>(name ?? '');
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror the parent-supplied name (authoritative) so the shown name survives
  // the TypeAhead's option churn and parent re-renders.
  useEffect(() => { setDisplayName(name ?? ''); }, [name]);

  const runSearch = (term: string) => {
    if (timer.current) clearTimeout(timer.current);
    const q = term.trim();
    if (q.length < MIN_CHARS) { setOptions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const rows = await searchPorts(q);
        setOptions(rows.map((r) => ({ value: r.value, label: r.label, description: r.description })));
      } catch { setOptions([]); }
    }, DEBOUNCE_MS);
  };

  return (
    <div className="flex-1 min-w-0">
      <PermissionAccessProvider accessLevel={2}>
        <TypeAhead
          freeText
          multiple={false}
          value={displayName}
          options={options}
          onChange={(text) => { setDisplayName(text); onChange(0, text); }}
          onSearch={runSearch}
          onSelect={(opt) => { setDisplayName(opt.label); onChange(Number(opt.value), opt.label); }}
          showDescriptions
          compact
          placeholder="Search port…"
          ariaLabel={ariaLabel}
          className="w-full"
        />
      </PermissionAccessProvider>
    </div>
  );
};

export default PortLookupField;
